import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next/';
import { subMonths } from 'date-fns';

import PreprUtil from '@utils/PreprUtil';
import { useNewsletter } from '@hooks/useNewsletter';

import NewsList from '@components/lists/NewsList/NewsList';
import styles from './Archive.module.scss';
import { NewsModule } from 'modules/NewsModule/NewsModule.service';
import NewsletterSignupBlock from '@components/containers/NewsletterSignupBlock/NewsletterSignupBlock';
import { Meta } from '@components/layout/Meta/Meta';
import AllTimeTopNewsList from '@components/lists/AllTimeTopNewsList/AllTimeTopNewsList';
import HighlightedNewsList from '@components/lists/HighlightedNewsList/HighlightedNewsList';

import {
  DestinationLinksProvider,
  FooterLinksProvider,
  SiteLinksProvider,
  useLocale,
} from '@hooks/index';
import { GenericService, GraphqlService, MailchimpService, PersonalService } from '@services/index';
import {
  ADVERTISEMENT_DEVICE,
  ADVERTISEMENT_TYPE,
  FB_APP_ID,
  NavbarTabsTypes,
  PageProps,
  REVALIDATION_TIME,
} from '@constants/index';

import { INewsArchivePublicationFields, INewsArticlePreviewFields } from 'modules/NewsModule';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import Layout from '@components/layout/Layout/Layout';
import Button from '@components/ui/Button/Button';
import { getRoute } from '@constants/routes';
import { Advertisement } from '@components/layout/Advertisement/Advertisement';
import { GetStaticPropsContext } from 'next';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<INewsArchivePublicationFields> {
  articles: INewsArticlePreviewFields[];
}

const NewsArchivePage = ({ fields, destinationLinks, siteLinks, footerLinks, articles }: Props) => {
  const { t } = useTranslation();
  const renderNewsletter = useNewsletter();
  const [renderAmount, setRenderAmount] = useState(15);
  const locale = useLocale();

  const handleLoadMore = React.useCallback(() => {
    setRenderAmount(renderAmount + 15);
  }, [renderAmount]);

  const getArticles = useCallback(
    (maxNumber?: number) => {
      return PreprUtil.formatArticles(
        articles,
        getRoute(locale as string, 'NEWS'),
        maxNumber,
        true,
        true,
      );
    },
    [articles],
  );

  const getTrendingArticles = useCallback(
    (maxNumber?: number) => {
      const _articles = articles.filter(
        (art: any) => art._created_on && new Date(art._created_on) > subMonths(new Date(), 3),
      );

      return PreprUtil.formatArticles(
        _articles.map(PreprUtil.mapViews).sort((a, b) => Number(b._views) - Number(a._views)), // sort high to low
        getRoute(locale as string, 'NEWS'),
        maxNumber,
        false,
        true,
      );
    },
    [articles],
  );

  const getMostPopularArticles = useCallback((maxNumber?: number) => {
    return PreprUtil.formatArticles(
      articles.map(PreprUtil.mapViews).sort((a, b) => Number(b._views) - Number(a._views)), // sort high to low
      getRoute(locale as string, 'NEWS'),
      maxNumber,
      false,
      true,
    );
  }, articles);

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout selectedTab={NavbarTabsTypes.NEWS} paddings>
              <div className={styles.NewsArchivePage}>
                <Meta
                  title={fields.meta_title || fields.title}
                  description={fields.meta_description || fields.title}
                />
                <div>
                  <Advertisement
                    className={styles.Advertisement}
                    advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                    advertisementDevice={ADVERTISEMENT_DEVICE.MOBILE}
                  />
                </div>
                <div className="container">
                  <HighlightedNewsList articles={getArticles(5)} />
                  <div>
                    <Advertisement
                      advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                      advertisementDevice={ADVERTISEMENT_DEVICE.MOBILE}
                    />
                  </div>
                  <NewsList
                    title={t('DOMAIN.NEWS.TRENDING')}
                    icon={require('@assets/images/icons/lightning.svg')}
                    articles={getTrendingArticles(10)}
                    size="lg"
                    className="mt-lg"
                  />
                  <Advertisement
                    advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                    advertisementDevice={ADVERTISEMENT_DEVICE.DESKTOP}
                  />
                  {renderNewsletter && (
                    <div className={styles.Newsletter}>
                      <NewsletterSignupBlock />
                    </div>
                  )}
                  <AllTimeTopNewsList articles={getMostPopularArticles()} className="mt-xl" />
                  <Advertisement
                    advertisementType={ADVERTISEMENT_TYPE.ARTICLE}
                    advertisementDevice={ADVERTISEMENT_DEVICE.DESKTOP}
                  />
                  <NewsList
                    title={t('DOMAIN.NEWS.BEST_ARTICLES')}
                    icon={require('@assets/images/icons/thumbs-up.svg')}
                    articles={getArticles(renderAmount)}
                    size="lg"
                    className="mt-xl"
                  />

                  {renderAmount < getArticles().length && (
                    <div className={styles.ButtonWrapper}>
                      <Button
                        iconPosition="left"
                        onPress={handleLoadMore}
                        className={styles.Button}>
                        {t('DOMAIN.BUCKETLIST.LOAD_MORE')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default NewsArchivePage;

export const getStaticProps = async ({ locale, preview }: GetStaticPropsContext) => {
  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const page = await NewsModule.getArchive();

  if (!page) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: page,
    revalidate: REVALIDATION_TIME,
  };
};
